const JPEG_QUALITY = 0.9;
const IMAGE_EXTENSION_RE = /\.[a-z0-9]+$/i;
const HEIC_MIMES = new Set(["image/heic", "image/heif"]);

function toJpegFileName(name: string): string {
  return name.replace(IMAGE_EXTENSION_RE, ".jpg");
}

/**
 * Converts a HEIC file to JPEG using heic-to.
 */
export async function convertHeicToJpeg(file: File): Promise<File> {
  const { heicTo } = await import("heic-to");
  const blob = await heicTo({
    blob: file,
    type: "image/jpeg",
    quality: JPEG_QUALITY,
  });
  return new File([blob], toJpegFileName(file.name), { type: "image/jpeg" });
}

/**
 * Converts a HEIC file to JPEG if needed, passing through non-HEIC files unchanged.
 */
export async function convertHeicIfNeeded(file: File): Promise<File> {
  let detected: { mime: string; ext: string } | undefined;
  try {
    const { fileTypeFromBlob } = await import("file-type");
    detected = await fileTypeFromBlob(file);
  } catch (error) {
    console.warn(
      "[heicConversion] fileTypeFromBlob failed, skipping HEIC check",
      {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        error,
      },
    );
    return file;
  }
  const isHeic = detected?.mime != null && HEIC_MIMES.has(detected.mime);

  if (!isHeic) return file;

  try {
    return await convertHeicToJpeg(file);
  } catch {
    throw new Error("Failed to convert HEIC image. Please try a JPG or PNG.");
  }
}
